"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Landmark, Building2, Scale, GraduationCap, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface SolutionItem {
  id: string;
  pillar: string;
  title: string;
  description: string;
  iconName: string;
  points: string[];
  to: string;
  ctaText: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const solutionsData: SolutionItem[] = [{
  id: "sol-gobierno",
  pillar: "Gobierno",
  title: "Soluciones para gobierno",
  description: "Asesoría técnica y contable orientada a la rendición de cuentas, control presupuestal y armonización contable en la administración pública.",
  iconName: "landmark",
  points: ["Contabilidad gubernamental y cumplimiento normativo CONAC", "Control de tesorería municipal y fiscalización preventiva", "Acompañamiento integral en licitaciones públicas"],
  to: "/GovernmentPage",
  ctaText: "Ver soluciones de gobierno",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_administration_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_administration_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/government_administration_session.png-wvc-sizes",
    alt: "Sesión administrativa de gestión pública gubernamental"
  }
}, {
  id: "sol-empresa",
  pillar: "Empresa",
  title: "Soluciones para empresas",
  description: "Consultoría financiera, contable y fiscal para salvaguardar la rentabilidad y mantener el cumplimiento riguroso ante las autoridades.",
  iconName: "building",
  points: ["Estrategia y dictaminación fiscal integral", "Diagnóstico financiero y elaboración de estados auditados", "Gestión especializada para empresas y estaciones de servicio"],
  to: "/CompaniesPage",
  ctaText: "Ver soluciones empresariales",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_financial_auditors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_financial_auditors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/corporate_financial_auditors.png-wvc-sizes",
    alt: "Auditores financieros revisando balances contables"
  }
}, {
  id: "sol-legal",
  pillar: "Legal",
  title: "Soluciones legales",
  description: "Defensa y asesoramiento jurídico especializado en derecho administrativo, fiscal, corporativo y agrario con criterio preventivo y resolutivo.",
  iconName: "scale",
  points: ["Litigio y defensa en derecho fiscal y administrativo", "Gobierno corporativo, actas y contratos mercantiles", "Asesoría jurídica en derecho agrario y propiedad"],
  to: "/LegalPage",
  ctaText: "Ver soluciones jurídicas",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/legal_contract_signing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/legal_contract_signing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/legal_contract_signing.png-wvc-sizes",
    alt: "Firma formal de contratos y documentos legales corporativos"
  }
}, {
  id: "sol-capacitacion",
  pillar: "Capacitación",
  title: "Soluciones en capacitación",
  description: "Programas ejecutivos, talleres prácticos y actualización técnica continua para funcionarios públicos y directivos corporativos.",
  iconName: "graduation",
  points: ["Talleres en normatividad gubernamental y disciplina financiera", "Capacitación en reformas fiscales vigentes y auditoría interna", "Cursos estructurados a la medida institucional o empresarial"],
  to: "/TrainingPage",
  ctaText: "Ver programas de capacitación",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_lecture_room.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_lecture_room.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_lecture_room.webp-wvc-sizes",
    alt: "Aula ejecutiva durante una jornada de capacitación profesional"
  }
}];
export default function Solutions() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "landmark":
        return <Landmark className="size-5 text-accent-foreground" aria-hidden="true" />;
      case "building":
        return <Building2 className="size-5 text-accent-foreground" aria-hidden="true" />;
      case "scale":
        return <Scale className="size-5 text-accent-foreground" aria-hidden="true" />;
      case "graduation":
      default:
        return <GraduationCap className="size-5 text-accent-foreground" aria-hidden="true" />;
    }
  };
  return <section id="resumen-de-soluciones" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase font-serif font-bold tracking-widest text-muted-foreground">
              Portafolio Institucional
            </span>
            <span className="text-muted-foreground font-bold">·</span>
            <span className="text-xs uppercase font-serif font-semibold tracking-wider text-muted-foreground">
              Gobierno · Empresa · Legal · Capacitación
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground uppercase">
            Resumen de soluciones
          </h2>

          <div className="w-12 h-[2px] bg-accent mt-4 mb-6" />

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            Ponemos a su disposición un equipo multidisciplinario enfocado en dar certeza técnica, legal y financiera a entidades públicas y privadas en México.
          </p>
        </div>

        {/* 4 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {solutionsData.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          delay: shouldReduceMotion ? 0 : i * 0.08,
          ease: "easeOut"
        }} className="h-full flex">
              <Card className="flex-1 flex flex-col justify-between bg-card text-card-foreground border border-border border-t-2 border-t-accent rounded-sm shadow-xs transition-[box-shadow,border-color] duration-300 hover:shadow-md hover:border-border">
                <div>
                  {/* Card Image */}
                  <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-sm border-b border-border bg-muted">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-[transform] duration-500 hover:scale-102" />
                    <div className="absolute top-3 left-3 bg-background/95 border border-border px-3 py-1 rounded-sm shadow-2xs">
                      <span className="font-serif text-xs font-bold uppercase tracking-wider text-foreground">
                        {item.pillar}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex size-10 items-center justify-center rounded-sm bg-accent/40 border border-border">
                        {renderIcon(item.iconName)}
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-wide text-foreground">
                        {item.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed mt-1">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <Separator className="mb-5 bg-border" />
                    <ul className="space-y-3 font-sans">
                      {item.points.map((point, pointIdx) => <li key={pointIdx} data-index={pointIdx} className="flex items-start gap-2.5 text-sm text-foreground/90">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" aria-hidden="true" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-0 pb-6">
                  <Button asChild variant="outline" className="w-full justify-between rounded-sm border-border bg-background hover:bg-accent/30 text-foreground font-serif uppercase text-xs font-semibold tracking-wider h-11 px-4 transition-colors">
                    <Link to={item.to}>
                      <span>{item.ctaText}</span>
                      <ArrowRight className="size-4 ml-2 text-foreground" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Global Institutional Advisory Strip */}
        <div className="mt-16 border border-border bg-card p-6 sm:p-8 rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="text-lg sm:text-xl font-serif font-bold uppercase text-foreground">
              ¿Requiere atención especializada para su caso?
            </h3>
            <p className="text-sm text-muted-foreground font-sans mt-1">
              Agende una consulta inicial con nuestros especialistas en Los Mochis o mediante sesión en línea.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-serif uppercase tracking-wider text-xs font-semibold h-10 px-6">
              <Link to="/ContactPage">
                Solicitar asesoría
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm border-border bg-background text-foreground hover:bg-muted font-serif uppercase tracking-wider text-xs font-semibold h-10 px-6">
              <Link to="/OnlineConsultingPage">
                Consultoría en línea
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}